import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { analyticsToken, loadAnalytics } from "./analytics";

/**
 * Cookie / on-device-storage consent (ekomloven § 3-15, GDPR art. 6(1)(a), 7).
 *
 * Categories:
 *   necessary — session cookies, display preference, this record, PWA shell cache.
 *               Exempt from consent (strictly necessary for the service the user asked for);
 *               the banner still *informs* about them.
 *   analytics — optional, cookieless Cloudflare Web Analytics. Off until the visitor
 *               opts in. Only offered at all when a token is configured.
 *
 * Bump CONSENT_VERSION when categories change so every visitor is asked again.
 * The decision is stored in localStorage (storing the consent itself is necessary).
 *   v1 — necessary only (2026-09-05)
 *   v2 — analytics category added (Cloudflare Web Analytics token set in production)
 */
export const CONSENT_VERSION = 2;
export const CONSENT_KEY = "bridewell.consent";

export interface ConsentRecord {
  v: number;
  analytics: boolean;
  /** ISO timestamp of the decision — the "record of consent" GDPR art. 7(1) asks for. */
  at: string;
}

function read(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const r = JSON.parse(raw) as Partial<ConsentRecord>;
    if (r.v !== CONSENT_VERSION || typeof r.analytics !== "boolean") return null;
    return { v: CONSENT_VERSION, analytics: r.analytics, at: typeof r.at === "string" ? r.at : new Date(0).toISOString() };
  } catch {
    return null;
  }
}

interface ConsentCtx {
  /** `null` until the visitor has decided (banner visible). */
  record: ConsentRecord | null;
  /** True when a Web Analytics token is configured, i.e. there is something to consent to. */
  analyticsAvailable: boolean;
  /** Effective: available AND opted in. */
  analytics: boolean;
  decide: (analytics: boolean) => void;
  settingsOpen: boolean;
  openSettings: () => void;
  closeSettings: () => void;
}

const Ctx = createContext<ConsentCtx | null>(null);

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [record, setRecord] = useState<ConsentRecord | null>(read);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const token = useMemo(analyticsToken, []);
  const analyticsAvailable = token !== null;
  const analytics = analyticsAvailable && record?.analytics === true;

  useEffect(() => {
    if (analytics && token) loadAnalytics(token);
  }, [analytics, token]);

  const decide = useCallback((wantAnalytics: boolean) => {
    const r: ConsentRecord = { v: CONSENT_VERSION, analytics: analyticsAvailable && wantAnalytics, at: new Date().toISOString() };
    setRecord(r);
    try { localStorage.setItem(CONSENT_KEY, JSON.stringify(r)); } catch { /* private mode: decision lives for this session only */ }
  }, [analyticsAvailable]);

  const value = useMemo<ConsentCtx>(() => ({
    record, analyticsAvailable, analytics, decide, settingsOpen,
    openSettings: () => setSettingsOpen(true),
    closeSettings: () => setSettingsOpen(false),
  }), [record, analyticsAvailable, analytics, decide, settingsOpen]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useConsent(): ConsentCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useConsent outside ConsentProvider");
  return v;
}
